/**
 * EmbedSlideViewer — Read-only slide viewer for embedding via iframe
 * Receives slide data via postMessage from parent window
 * Renders shapes using SlideCanvas in read-only mode with navigation
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const SLIDE_W = 960;
const SLIDE_H = 540;

// Minimal read-only shape renderer (no drag/resize, just display)
function ReadOnlyShape({ shape }) {
  const [imgUrl, setImgUrl] = useState(shape.src || null);

  useEffect(() => {
    if (shape.type === 'image' && shape.storageKey && !shape.src) {
      fetch(`/api/work/documents/image-url?key=${encodeURIComponent(shape.storageKey)}`, { credentials: 'include' })
        .then(r => r.json()).then(d => setImgUrl(d.url)).catch(() => {});
    }
  }, [shape.storageKey, shape.type, shape.src]);

  const style = {
    position: 'absolute',
    left: shape.x,
    top: shape.y,
    width: shape.width || shape.w,
    height: shape.height || shape.h,
    backgroundColor: shape.fill && shape.fill !== 'transparent' ? shape.fill : undefined,
    border: shape.stroke ? `${shape.strokeWidth || 1}px solid ${shape.stroke}` : undefined,
    borderRadius: shape.type === 'ellipse' ? '50%' : (shape.radius ? `${shape.radius}px` : undefined),
    opacity: shape.opacity ?? 1,
    zIndex: shape.zIndex || 0,
    transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
    overflow: 'hidden',
  };

  if (shape.type === 'image') {
    return (
      <div style={style}>
        {imgUrl && <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      </div>
    );
  }

  if (shape.type === 'line') {
    return (
      <div style={{ ...style, height: Math.max(shape.strokeWidth || 2, 2), backgroundColor: shape.stroke || '#333' }} />
    );
  }

  // Text / rect / ellipse
  return (
    <div style={style}>
      {shape.text && (
        <div style={{
          color: shape.textColor || '#333',
          fontSize: shape.fontSize || 16,
          fontWeight: shape.fontWeight || 'normal',
          fontStyle: shape.fontStyle || 'normal',
          fontFamily: shape.fontFamily || 'sans-serif',
          textAlign: shape.textAlign || 'left',
          padding: '4px 8px',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: shape.verticalAlign === 'center' ? 'center' : shape.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.3,
        }}>
          <span>{shape.text}</span>
        </div>
      )}
    </div>
  );
}

export default function EmbedSlideViewer() {
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [scale, setScale] = useState(1);
  const [presenting, setPresenting] = useState(false);
  const containerRef = useRef(null);

  // Listen for slide data from parent window
  useEffect(() => {
    function onMessage(e) {
      if (e.data?.type === 'IMAGIA_SLIDES') {
        setSlides(e.data.slides || []);
        setActiveSlide(0);
      }
    }
    window.addEventListener('message', onMessage);
    // Signal ready to parent
    window.parent?.postMessage({ type: 'JUBLY_EMBED_READY' }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Also accept slides via URL hash (for simple cases)
  useEffect(() => {
    try {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const data = JSON.parse(decodeURIComponent(hash));
        if (Array.isArray(data)) setSlides(data);
      }
    } catch {}
  }, []);

  // Compute scale
  useEffect(() => {
    function updateScale() {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = Math.min(rect.width / SLIDE_W, rect.height / SLIDE_H);
      setScale(s);
    }
    updateScale();
    const obs = new ResizeObserver(updateScale);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [presenting]);

  // Keyboard navigation
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setActiveSlide(i => Math.min(i + 1, slides.length - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSlide(i => Math.max(i - 1, 0));
      } else if (e.key === 'Escape') {
        setPresenting(false);
      } else if (e.key === 'f' || e.key === 'F5') {
        e.preventDefault();
        setPresenting(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length]);

  const currentSlide = slides[activeSlide];

  if (slides.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 text-gray-400 text-sm">
        Waiting for slides...
      </div>
    );
  }

  // Fullscreen presenting mode
  if (presenting) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center cursor-none"
        ref={containerRef}
        onClick={() => setActiveSlide(i => Math.min(i + 1, slides.length - 1))}>
        <div className="relative" style={{ width: SLIDE_W * scale, height: SLIDE_H * scale }}>
          <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative', backgroundColor: currentSlide?.bg || '#ffffff', borderRadius: 0 }}>
            {currentSlide?.shapes?.map(shape => <ReadOnlyShape key={shape.id} shape={shape} />)}
          </div>
        </div>
        {/* Slide counter */}
        <div className="absolute bottom-4 right-4 text-white/50 text-xs">{activeSlide + 1} / {slides.length}</div>
        <button onClick={(e) => { e.stopPropagation(); setPresenting(false); }} className="absolute top-4 right-4 text-white/30 hover:text-white text-sm">ESC</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-gray-200 shrink-0">
        <button onClick={() => setActiveSlide(i => Math.max(i - 1, 0))} disabled={activeSlide === 0}
          className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
        </button>
        <span className="text-xs text-gray-500 font-medium min-w-[60px] text-center">{activeSlide + 1} / {slides.length}</span>
        <button onClick={() => setActiveSlide(i => Math.min(i + 1, slides.length - 1))} disabled={activeSlide >= slides.length - 1}
          className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
        </button>
        <div className="flex-1" />
        <button onClick={() => setPresenting(true)} className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-700 font-medium">
          Present
        </button>
      </div>

      {/* Slide area */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="relative shadow-xl rounded-lg overflow-hidden"
          style={{ width: SLIDE_W * scale, height: SLIDE_H * scale, backgroundColor: currentSlide?.bg || '#ffffff' }}>
          <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative', overflow: 'hidden' }}>
            {currentSlide?.shapes?.map(shape => <ReadOnlyShape key={shape.id} shape={shape} />)}
            {(!currentSlide?.shapes || currentSlide.shapes.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-lg select-none">
                Empty slide
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Slide filmstrip */}
      {slides.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-white border-t border-gray-200 overflow-x-auto shrink-0">
          {slides.map((slide, i) => (
            <button key={slide.id || i} onClick={() => setActiveSlide(i)}
              className={`shrink-0 rounded border-2 overflow-hidden transition-all ${i === activeSlide ? 'border-indigo-500 shadow-md' : 'border-gray-200 hover:border-gray-400'}`}
              style={{ width: 96, height: 54 }}>
              <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${96 / SLIDE_W})`, transformOrigin: 'top left', position: 'relative', backgroundColor: slide.bg || '#ffffff' }}>
                {slide.shapes?.map(shape => <ReadOnlyShape key={shape.id} shape={shape} />)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
